// Steps/ScheduleAndMode.js - Step 3 Component
import React, { useContext, useEffect } from 'react';
import { Box, Typography, Grid, FormControl, RadioGroup, FormControlLabel, Radio, FormGroup, Checkbox, Card, CardContent, Chip, Avatar, Alert, Button} from '@mui/material';
import { NotificationsActive, Sms, WhatsApp, Email, Schedule, SendTimeExtension } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { NotificationDataContext } from '../AddEditNotification';
import { formatDateTime } from '../../../../utils/dateUtils';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import { useForm, Controller } from 'react-hook-form';
import NavigationButton from './NavigationButton';

const ScheduleAndMode = () => {
    const { notificationData, setNotificationData ,initialState,steps,activeStep,setActiveStep, setCompletedSteps} = useContext(NotificationDataContext);
        
    const {control,watch,handleSubmit,formState:{isDirty,errors},setValue} = useForm({
        mode: 'onChange',
        defaultValues: {            
            deliveryModes: initialState.deliveryModes,
            scheduleType: initialState.scheduleType,
            scheduledDateTime: initialState.scheduledDateTime,
        }
    });
    useEffect(() => {
        if(notificationData.deliveryModes){
            setValue('deliveryModes',notificationData.deliveryModes,{shouldValidate: true,shouldDirty: true});
        }
        if(notificationData.scheduleType){
            setValue('scheduleType',notificationData.scheduleType,{shouldValidate: true,shouldDirty: true});
        }
        if(notificationData.scheduledDateTime){
            setValue('scheduledDateTime',notificationData.scheduledDateTime,{shouldValidate: true,shouldDirty: true});
        }
    },[])
    const completeStep = (data) => {
        console.log('Audience Selection Data:', data);
        setNotificationData(prev => ({
            ...prev,
            deliveryModes: data.deliveryModes,
            scheduleType: data.scheduleType,
            scheduledDateTime: data.scheduledDateTime
        }))
        if (activeStep < steps.length - 1) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            setCompletedSteps((prev) => {
                prev.add(activeStep);
                return new Set(prev);
            });
        }
    }
    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
        }
    };

    // Previous code - Multiple selection allowed
    // const handleDeliveryModeChange = (mode) => (event) => {
    //     if (event.target.checked) {
    //         setNotificationData(prev => ({
    //             ...prev,
    //             deliveryModes: [...prev.deliveryModes, mode]
    //         }));
    //     } else {
    //         // Don't allow unchecking if it's the only mode selected
    //         if (notificationData.deliveryModes.length > 1) {
    //             setNotificationData(prev => ({
    //                 ...prev,
    //                 deliveryModes: prev.deliveryModes.filter(m => m !== mode)
    //             }));
    //         }
    //     }
    // };

    // Current code - Single selection only (for future multiple selection, uncomment above)
    const handleDeliveryModeChange = (currentValues, event, newValue) => {
        console.log("event", event);
        console.log("newValue", newValue);
        console.log("currentValues", currentValues);
        
        if (event.target.checked) {
            // Replace with single mode (allow only one selection at a time)
            return [newValue];
        } else {
            // Don't allow unchecking if it's the only mode selected
            // At least one mode must be selected
            if (currentValues.length > 1) {
                return currentValues.filter(mode => mode !== newValue);
            }
            // Return current values if trying to uncheck the last item
            return currentValues;
        }
    };

    const deliveryOptions = [
        {
            value: 'push',
            label: 'Push Notification',
            description: 'Send instant push notifications to mobile app',
            icon: <NotificationsActive sx={{ fontSize: 30 }} />,
            available: notificationData.audienceType !== 'csvUpload', // Not available for CSV upload
            color: '#4CAF50'
        },
        {
            value: 'sms',
            label: 'SMS',
            description: 'Send text messages to mobile numbers',
            icon: <Sms sx={{ fontSize: 30 }} />,
            available: false,
            color: '#FF9800',
            comingSoon: true
        },
        {
            value: 'whatsapp',
            label: 'WhatsApp',
            description: 'Send messages via WhatsApp Business API',
            icon: <WhatsApp sx={{ fontSize: 30 }} />,
            available: true, // Available for all audience types including CSV upload
            color: '#25D366',
            // comingSoon: true
        },
        {
            value: 'email',
            label: 'Email',
            description: 'Send email notifications',
            icon: <Email sx={{ fontSize: 30 }} />,
            available: false,
            color: '#2196F3',
            comingSoon: true
        }
    ];

    const selectedAudienceCount = notificationData.audienceType === 'csvUpload'
        ? notificationData.csvUploadData?.phoneNumbers?.length || 0
        : notificationData.audienceType === 'customer' 
        ? notificationData.customerFilters.selectedCustomers.length
        : notificationData.expertFilters.selectedExperts.length;

    return (
        <Box component={'form'} onSubmit={handleSubmit(completeStep)}>
            <Card>
                <CardContent sx={{ height:'max-content' }}>
                    <Grid container spacing={1}>
                        {/* Delivery Modes Section */}
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined" sx={{ height: 'fit-content' }}>
                                <CardContent sx={{py:0}}>
                                    <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                        <Typography variant="subtitle1" gutterBottom sx={{ 
                                            fontWeight: 600, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                        }}>
                                            <SendTimeExtension color="primary" />
                                            Delivery Channels
                                        </Typography>
                                        {/* Selected Modes Summary */}
                                        {notificationData.deliveryModes.length > 0 && (
                                            <Box sx={{display:'flex',gap:1,alignItems:'center', bgcolor: 'success.50',  }}>
                                                <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'success.800', mb: 0.5 }}>
                                                    Selected Delivery Channels:
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {notificationData.deliveryModes.map((mode) => {
                                                        const option = deliveryOptions.find(opt => opt.value === mode);
                                                        return (
                                                            <Chip
                                                                key={mode}
                                                                label={option?.label}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    height: 20,
                                                                    bgcolor: 'green',
                                                                    color: '#fff'
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Choose how to deliver your notification. At least one channel must be selected.
                                    </Typography>

                                    <FormControl component="fieldset" fullWidth>
                                        <Controller
                                            name='deliveryModes'
                                            control={control}
                                            rules={{
                                                required: 'Select Delivery Mode',
                                                validate: (value) => {
                                                    if (value.length === 0) {
                                                        return 'At least one delivery mode must be selected';
                                                    }
                                                    return true;
                                                }
                                            }}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <FormGroup>
                                                    {deliveryOptions.map((option) => (
                                                        <Card
                                                            key={option.value}
                                                            variant="outlined"
                                                            sx={{
                                                                mb: 1,
                                                                border: '1px solid',
                                                                borderColor: notificationData.deliveryModes.includes(option.value) 
                                                                    ? 'primary.main' 
                                                                    : option.available ? 'grey.300' : 'grey.200',
                                                                bgcolor: notificationData.deliveryModes.includes(option.value) 
                                                                    ? 'primary.50' 
                                                                    : option.available ? 'background.paper' : 'grey.50',
                                                                opacity: option.available ? 1 : 0.6,
                                                                cursor: option.available ? 'pointer' : 'not-allowed'
                                                            }}
                                                        >
                                                            <CardContent sx={{ p: 1, '&:last-child': { pb: 0 } }}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            // checked={notificationData.deliveryModes.includes(option.value)}
                                                                            checked={value.includes(option.value)}
                                                                            // onChange={handleDeliveryModeChange(option.value)}
                                                                             onChange={(event) => {
                                                                                let nValue = handleDeliveryModeChange(value, event, option.value);
                                                                                console.log("nValue",nValue)
                                                                                onChange(nValue);
                                                                            }}
                                                                            // disabled={!option.available || value.includes(option.value)}
                                                                            disabled={!option.available}
                                                                            size="small"
                                                                        />
                                                                    }
                                                                    label={
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                                            <Avatar
                                                                                sx={{
                                                                                    width: 40,
                                                                                    height: 40,
                                                                                    bgcolor: option.available ? option.color : 'grey.400'
                                                                                }}
                                                                            >
                                                                                {React.cloneElement(option.icon, {
                                                                                    sx: { fontSize: 20, color: 'white' }
                                                                                })}
                                                                            </Avatar>
                                                                            <Box sx={{ flex: 1 }}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                    <Typography variant="subtitle2" sx={{ 
                                                                                        fontWeight: 600, 
                                                                                        fontSize: '0.8rem' 
                                                                                    }}>
                                                                                        {option.label}
                                                                                    </Typography>
                                                                                    {option.comingSoon && (
                                                                                        <Chip 
                                                                                            label="Coming Soon" 
                                                                                            size="small" 
                                                                                            sx={{ 
                                                                                                height: 16, 
                                                                                                fontSize: '0.6rem',
                                                                                                bgcolor: 'warning.100',
                                                                                                color: 'warning.800'
                                                                                            }} 
                                                                                        />
                                                                                    )}
                                                                                </Box>
                                                                                <Typography 
                                                                                    variant="body2" 
                                                                                    color="text.secondary"
                                                                                    sx={{ fontSize: '0.7rem', mt: 0.5 }}
                                                                                >
                                                                                    {option.description}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    }
                                                                    sx={{ m: 0, width: '100%' }}
                                                                />
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </FormGroup>
                                            )}
                                        />
                                        
                                    </FormControl>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Grid container spacing ={1}>
                                {/* Schedule Section */}
                                <Grid item xs={12}>
                                    <Card variant="outlined" sx={{ height: 'fit-content' }}>
                                        <CardContent sx={{pb:'0px !important'}}>
                                            <Typography variant="subtitle1" gutterBottom sx={{ 
                                                fontWeight: 600, 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 1,
                                                
                                            }}>
                                                <Schedule color="primary" />
                                                Scheduling Options
                                            </Typography>

                                            <FormControl component="fieldset" fullWidth>
                                                <Controller
                                                    name="scheduleType"
                                                    control={control}
                                                    rules={{ required: "Please select schedule type" }}
                                                    // defaultValue={initialState.scheduleType}
                                                    render={({ field }) => (
                                                        <RadioGroup
                                                            // value={notificationData.scheduleType}
                                                            // onChange={handleScheduleTypeChange}
                                                            {...field}
                                                            sx={{ gap: 1 }}
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent:'space-between' }}>
                                                                <FormControlLabel
                                                                    value="now"
                                                                    control={<Radio />}
                                                                    label={
                                                                        <Box>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                                Send Now
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                                Deliver notification immediately
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    sx={{
                                                                        border: '1px solid',
                                                                        borderColor: notificationData.scheduleType === 'now' ? 'primary.main' : 'grey.300',
                                                                        borderRadius: 1,
                                                                        p: 1,
                                                                        m: 0,
                                                                        mb: 1,
                                                                        bgcolor: notificationData.scheduleType === 'now' ? 'primary.50' : 'transparent'
                                                                    }}
                                                                />

                                                                <FormControlLabel
                                                                    value="later"
                                                                    control={<Radio />}
                                                                    label={
                                                                        <Box>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                                Schedule for Later
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                                Set a specific date and time
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    sx={{
                                                                        border: '1px solid',
                                                                        borderColor: notificationData.scheduleType === 'later' ? 'primary.main' : 'grey.300',
                                                                        borderRadius: 1,
                                                                        p: 1,
                                                                        m: 0,
                                                                        bgcolor: notificationData.scheduleType === 'later' ? 'primary.50' : 'transparent'
                                                                    }}
                                                                />
                                                            </Box>
                                                        </RadioGroup>
                                                    )}
                                                />
                                                
                                            </FormControl>

                                            {watch('scheduleType') === 'later' && (
                                                <Grid container spacing={1} sx={{ my: 1 }}>
                                                    <Grid item xs={12} sm={watch('scheduledDateTime') && 5}>
                                                        <Controller
                                                            name="scheduledDateTime"
                                                            control={control}
                                                            rules={{
                                                                required: watch('scheduleType') === 'later' ? 'Please select date and time' : false,
                                                                validate: (value) => {
                                                                    if (watch('scheduleType') === 'later' && value && dayjs(value).isBefore(dayjs())) {
                                                                        return 'Scheduled time must be in the future';
                                                                    }
                                                                    return true;
                                                                }
                                                            }}
                                                            render={({ field, fieldState: { error } }) => (
                                                                <DateTimePicker
                                                                    label="Schedule Date & Time"
                                                                    {...field}
                                                                    minDateTime={dayjs().add(1, 'minute')}
                                                                    closeOnSelect={false}
                                                                    slotProps={{
                                                                        textField: {
                                                                            size: 'small',
                                                                            fullWidth: true,
                                                                            required: watch('scheduleType') === 'later',
                                                                            error: !!error,
                                                                            helperText: error?.message,
                                                                            InputProps: {
                                                                                style: { fontSize: '0.75rem' }
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </Grid>                                            
                                                    {watch('scheduledDateTime') && (
                                                        <Grid item xs={12} sm={7}>
                                                            <Alert severity="info" sx={{ fontSize: '0.7rem' }}>
                                                                <strong>Scheduled for:</strong> {formatDateTime(watch('scheduledDateTime'))}
                                                            </Alert>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                

                                {/* Summary Section */}
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                            Configuration Summary:
                                        </Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                            
                                                <Typography sx={{fontWeight:600,fontSize:'0.8rem'}}>Recipients: <Box component={'span'} sx={{fontWeight:500,fontSize:'0.75rem',ml:1}}>{selectedAudienceCount} {notificationData.audienceType}(s)</Box></Typography> 
                                            
                                            
                                                <Typography sx={{fontWeight:600,fontSize:'0.8rem',color:(errors?.scheduleType||errors?.scheduledDateTime)&&'red'}}>Schedule: 
                                                    <Box component={'span'} sx={{fontWeight:500,fontSize:'0.75rem',ml:1}}>
                                                        {watch('scheduleType') === 'now' ? 'Send immediately' : 
                                                                watch('scheduledDateTime') ? `${formatDateTime(watch('scheduledDateTime'))}` :
                                                                'Please select date and time'
                                                        }
                                                    </Box>
                                                </Typography> 
                                            
                                            
                                                <Typography sx={{fontWeight:600,color:errors?.deliveryModes&&'red',fontSize:'0.8rem'}}>Delivery Channels:
                                                    <Box component={'span'} sx={{fontWeight:500,fontSize:'0.75rem',ml:1}}>
                                                        {
                                                            errors.deliveryModes ?
                                                            // <Typography component="span" color="error.main">
                                                                errors.deliveryModes.message
                                                            :
                                                        watch('deliveryModes').map(mode => 
                                                            deliveryOptions.find(opt => opt.value === mode)?.label
                                                        ).join(', ')
                                                        }
                                                    </Box>   
                                                </Typography>                                          
                                        </Box>
                                    </Alert>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <NavigationButton isDirty={isDirty} />
        </Box>
    );
};

export default ScheduleAndMode;